<script setup lang="ts">
// Data-protection action (RDT-474): hard-deletes a participant with all their data on every service
// (RDT-470 chain) — no REVOKED status, the activities go with the row. Rendered only for holders of
// the PERSOLOG_ONLINE_DATA_PROTECTION realm role; the backend enforces the same rule, so the button
// mirrors the server-side check rather than being the protection itself.
import { computed, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { ProfileAdministrationAPI } from "../../api";
import { keycloak } from "../../main.ts";
import { useConfirmedParticipantAction } from "../../composables/useConfirmedParticipantAction.ts";
import { Participant } from "../../types/PersologOnlineTypes.ts";

const props = defineProps({
    participant: {
        type: Object as PropType<Participant>,
        required: true,
    },
});

const emit = defineEmits<{ deleted: [] }>();

const { t } = useI18n();

const canDelete = Boolean(keycloak.hasRealmRole("PERSOLOG_ONLINE_DATA_PROTECTION"));

// Pseudo-deleted direct-link participants keep their row with status DELETED; the backend refuses
// deleting those again (409), so don't offer it.
const disabled = computed(() => !props.participant.id || props.participant.status === "DELETED");

const confirmGroup = computed(() => `delete-participant-${props.participant.id}`);

const { loading, askForConfirmation } = useConfirmedParticipantAction({
    participant: () => props.participant,
    messages: "participants.delete",
    confirmGroup: () => confirmGroup.value,
    logMessage: "Could not delete the participant",
    action: (participantId) => ProfileAdministrationAPI.deleteParticipant(participantId),
    onDone: () => emit("deleted"),
});
</script>

<template>
    <template v-if="canDelete">
        <ConfirmDialog :group="confirmGroup" />
        <Button
            :data-cy="`delete-participant-button-${participant.id}`"
            icon="pi pi-trash"
            rounded
            outlined
            severity="danger"
            class="mr-2"
            :aria-label="t('participants.delete.action')"
            v-tooltip="t('participants.delete.action')"
            :loading="loading"
            :disabled="disabled"
            @click="askForConfirmation"
        />
    </template>
</template>
